/* eslint-disable react-hooks/rules-of-hooks */
import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Switch,
  Autocomplete,
  InputAdornment,
  Typography,
} from '@mui/material';
import {
  Agency,
  Brand,
  CannedResponse,
  ChangeEventType,
  Fee,
  InvestmentRequirement,
  InvitationErrors,
  InviteInfo,
  Salesperson,
} from '../../types';
import { FloatingButton, PrimaryButton } from '../Buttons';
import CircularLoading from '../CircularLoading';
import {
  ACRONYM_MAX_LENGTH,
  ACRONYM_MIN_LENGTH,
  ADMIN,
  IS_DIY_ADZ,
  MARKETING_MANAGER,
  SUPER_ADMIN,
} from '../../utils';
import styles from '../../assets/styles/components/Forms/Form.module.scss';
import { createBrand, editBrand, getSingleBrand } from '../../services/brand';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context';
import { getAgencies, getAgencyUsers } from '../../services/agency';
import {
  getAllBrandManagers,
  getAllMarketingManagers,
  getAllSalesperson,
} from '../../services/salesperson';
import { useDispatch, useSelector } from 'react-redux';
import { XsOnly } from '../../utils/breakpoints';
import PageHeader from '../PageHeader';
import AddIcon from '@mui/icons-material/Add';
import BrandUserForm from './BrandUserForm';
import { setBrand, setBrands, toggleAlert } from '../../redux/actions';
import { inviteSalesperson } from '../../services/invitation';
import InviteModal from '../Modal/InviteModal';
import { humanizeString } from '../../utils/stringModifier';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
import { createOrUpdateFee } from '../../services/fee';
import ConfirmNavigationModal, { usePrompt } from '../Navigation/RoutePrompt';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import GroupRemoveOutlinedIcon from '@mui/icons-material/GroupRemoveOutlined';
import { DiySettings } from '../../types/IDiy';
import {
  createOrUpdateDiySettings,
  getSingleDiySetting,
} from '../../services/diy';
import {
  AnalyticTools,
  BusinessType,
  brandIndustries,
  businessTypes,
} from '../../utils/helpers/BrandHelpers';
import { useForm } from 'react-hook-form';
import { errorMessageParser } from '../../utils/helpers/ToastHelper';
import { adsProvider } from '../../utils/constants/facebookAds';

const MAX_USER_COUNT: number = 10;

const BrandForm: React.FC = () => {
  const {
    formState: { errors },
    register,
    handleSubmit,
    clearErrors,
    setValue,
  } = useForm({ mode: 'onSubmit' });
  const brand: Brand = useSelector((state: any) => state.brand?.brand);
  const brands: Brand[] = useSelector((state: any) => state.brands?.brands);
  const dispatch = useDispatch();
  const pathLocation = useLocation();
  const { state } = useContext(AuthContext);
  const role = state.role;
  const isAdmin = role === ADMIN;
  const isSuperAdmin = role === SUPER_ADMIN;
  const isMarketingManager = role === MARKETING_MANAGER;
  const roleBasedId = state.roleBasedId;
  const capabilities = state.capabilities;
  const withAgencyAccess = state?.withAgencyAccess;
  const pathnames = pathLocation.pathname.split('/').filter((x) => x);
  const isEdit =
    pathnames.includes('preferencez') ||
    pathnames.includes('edit') ||
    pathnames.includes('preferences');
  const navigate = useNavigate();
  const canCreateBrand =
    !(isSuperAdmin || isAdmin) && capabilities?.createBrands;
  const [loading, setLoading] = useState<boolean>(false);
  const [defaultCannedResponse] = useState<CannedResponse[]>([
    {
      message: `Hi <<lead>>! Good day, I'm <<salesperson>> of <<brand>>, how can I help you?`,
      isDeleted: false,
    },
    {
      message: `Hi <<lead>> you want to schedule a call? Here's my Calendly link <<calendly_link>>.`,
      isDeleted: false,
    },
    {
      message:
        'I am glad I was able to help. Please tell me if you have any more queries 🙂',
      isDeleted: false,
    },
  ]);
  const [formValues, setFormValues] = useState<Brand>({
    brand: '',
    acronym: '',
    allLeads: 'No',
    zipcodereq: 'No',
    financialreq: 'Yes',
    redirect: 'No',
    redirectlink: '',
    redirecttext: '',
    industryRequirement: 'No',
    schedulecall: 'No',
    industryQuestion: '',
    calendly: '',
    salesperson: {
      name: '',
      _id: '',
      email: '',
      role: '',
      user: {
        email: '',
      },
    },
    salespersonTitle: '',
    page: null,
    phoneNumReq: 'No',
    budgetOffset: 100,
    budgetPercentageFee: 18,
    cannedResponses: [
      {
        message: `Hi <<lead>>! Good day, I'm <<salesperson>> of <<brand>>, how can I help you?`,
        isDeleted: false,
      },
      {
        message: `Hi <<lead>> you want to schedule a call? Here's my Calendly link <<calendly_link>>.`,
        isDeleted: false,
      },
      {
        message:
          'I am glad I was able to help. Please tell me if you have any more queries 🙂',
        isDeleted: false,
      },
    ],
    manager: {
      name: '',
      _id: '',
      email: '',
      role: '',
      user: {
        email: '',
      },
    },
    marketingManager: {
      name: '',
      _id: '',
      email: '',
      role: '',
      user: {
        email: '',
      },
    },
    active: true,
    paused: false,
    lastPausedAt: null,
    facebookBusinessIds: [],
    googleCustomerIds: [],
    allowLocations: false,
    allowLocationsSetupPayment: false,
    agency: {
      _id: '',
      agency: '',
    },
    __type: 'brand',
    allowSetupDepositAccount: false,
    allowSetupPayment: false,
    industry: '',
    businessType: BusinessType.FRANDEV,
    diy: IS_DIY_ADZ,
    users: [],
    analyticTools: [AnalyticTools.TRENDZ],
    locationLabel: 'location',
  });
  const [brandFeesForm, setBrandFeesForm] = useState<Fee[]>([]);
  const [locationFeesForm, setLocationFeesForm] = useState<Fee[]>([]);
  const [investmentRequirement, setInvestmentRequirement] =
    useState<InvestmentRequirement>({
      text: '',
      num: 0,
      top: 0,
    });
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [salespersons, setSalespersons] = useState<Salesperson[]>([]);
  const [managers, setManagers] = useState<Salesperson[]>([]);
  const [marketingManagers, setMarketingManagers] = useState<Salesperson[]>([]);
  const [showSalespersonForm, setShowSalespersonForm] =
    useState<boolean>(false);
  const [showManagerForm, setShowManagerForm] = useState<boolean>(false);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [inviteLoading, setInviteLoading] = useState<boolean>(false);
  const [invitationErrors, setInvitationErrors] = useState<InvitationErrors>({
    email: '',
  });
  const [openInviteModal, setOpenInviteModal] = useState<boolean>(false);
  const [invitationInfo, setInvitationInfo] = useState<InviteInfo>({
    name: '',
    email: '',
    id: '',
    role: '',
  });
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [openConfirmNavModal, setOpenConfirmNavModal] =
    useState<boolean>(false);
  const [autoUnblockingTx, setAutoUnblockingTx] = useState<any>(null);
  const [showMultipleUserForms, setShowMultipleUserForms] = useState<boolean[]>(
    [false],
  );
  const [formCount, setFormCount] = useState<number>(1);
  const [diySettingForm, setDiySettingForm] = useState<DiySettings>({
    ref: brand?._id,
    docModel: 'Brand',
    allowTrafficAds: true,
    allowLeadAds: true,
    allowMessengerAds: false,
    allowAppAds: false,
    allowAllGeolocation: true,
    locationTypes: [],
    excludedLocationTypes: [],
    excludedGeolocations: null,
    excludeGeolocation: false,
    defaultUrl: '',
    defaultEmploymentUrl: '',
    defaultCalendarUrl: '',
    defaultPrivacyPolicyUrl: '',
    appDeepLink: '',
    maxCityRadius: 50,
    maxAddressRadius: 50,
    allowSetupOwnAccount: false,
    geolocation: null,
    facebookPage: null,
    instagramAccount: null,
    fbAdAccount: null,
    defaultLocation: null,
    defaultUser: null,
    targetCountries: [{ code: 'US', label: 'United States', phone: '1' }],
  });
  const [brandDiySettings, setBrandDiySettings] = useState<DiySettings>(null);

  useEffect(() => {
    if (!isEdit) {
      setValue('businessType', BusinessType.FRANDEV);
      setValue('budgetOffset', 100);
    }
  }, [isEdit]);

  useEffect(() => {
    if (formValues?.__type === 'location' && brand) {
      getBrandDiySettings(brand);
    }
  }, [brand, formValues?.__type]);

  usePrompt(setOpenConfirmNavModal, setAutoUnblockingTx, isDirty);

  useEffect(() => {
    resetForm();
    resetDiyForm();

    allAgenciesAndSalesperson();

    if (isEdit && brand?._id) {
      getDiySettings(brand);
      getBrand(brand?._id);
    }
  }, [brand, isEdit]);

  const getBrand = async (brandId: string) => {
    try {
      setFetchLoading(true);

      const response: Brand = await getSingleBrand(brandId);

      setFormValues({
        ...response,
        cannedResponses: response?.cannedResponses || defaultCannedResponse,
      });
      setInvestmentRequirement(response.investmentRequirement);
      setFormCount(response?.users?.length || 1);
      setValue('businessType', response?.businessType);
      setValue('industry', response?.industry);
      setValue('agency', response?.agency?._id);
      setValue('acronym', response?.acronym);

      if (response.users?.length > 0) {
        response.users.forEach((user: Salesperson, index: number) => {
          setValue(`user-${index + 1}`, user?._id);
        });
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const allAgenciesAndSalesperson = async () => {
    try {
      setFetchLoading(true);

      const agencyResponse = await getAgencies(
        1,
        1000,
        undefined,
        undefined,
        undefined,
        IS_DIY_ADZ,
      );

      const agencies = agencyResponse.data.map((agency: Agency) => {
        return {
          _id: agency._id,
          agency: agency.agency,
          agencyId: agency.agencyId,
          defaultUser: agency.defaultUser,
          allowSetupPayment: agency.allowSetupPayment,
        };
      });

      if (agencies.length === 1) {
        setFormValues({ ...formValues, agency: agencies[0] });

        if (!(isAdmin || isSuperAdmin) && withAgencyAccess && !isEdit) {
          setDiySettingForm({
            ...diySettingForm,
            defaultUser: agencies[0]?.defaultUser,
          });
        }

        setValue('agency', agencies[0]?._id);
      }

      const agencyUsersReponse = await getAgencyUsers();

      const salespersonsResponse = await getAllSalesperson(
        1,
        1000,
        'active',
        undefined,
        undefined,
        undefined,
        IS_DIY_ADZ ? true : undefined,
        IS_DIY_ADZ,
      );
      const managersResponse = await getAllBrandManagers(
        1,
        1000,
        undefined,
        IS_DIY_ADZ,
      );
      const marketingManagersResponse = await getAllMarketingManagers(
        1,
        1000,
        undefined,
        IS_DIY_ADZ,
      );
      const agencyUsers = agencyUsersReponse.data.map(
        (agencyUser: Salesperson) => {
          return {
            _id: agencyUser._id,
            name: agencyUser.name,
            salespersonId: agencyUser.salespersonId,
            email: agencyUser.email,
            role: agencyUser.role,
            user: agencyUser.user,
          };
        },
      );
      const salespersons = salespersonsResponse.data.map(
        (salesperson: Salesperson) => {
          return {
            _id: salesperson._id,
            name: salesperson.name,
            salespersonId: salesperson.salespersonId,
            email: salesperson.email,
            role: salesperson.role,
            user: salesperson.user,
          };
        },
      );
      const managers = managersResponse.data.map((salesperson: Salesperson) => {
        return {
          _id: salesperson._id,
          name: salesperson.name,
          salespersonId: salesperson.salespersonId,
          email: salesperson.email,
          role: salesperson.role,
          user: salesperson.user,
        };
      });
      const marketingManagers = marketingManagersResponse.data.map(
        (marketingManager: Salesperson) => {
          return {
            _id: marketingManager._id,
            name: marketingManager.name,
            salespersonId: marketingManager.salespersonId,
            email: marketingManager.email,
            role: marketingManager.role,
            user: marketingManager.user,
          };
        },
      );

      setSalespersons(
        IS_DIY_ADZ && !isSuperAdmin && !isAdmin ? agencyUsers : salespersons,
      );
      setManagers(managers);
      setMarketingManagers(marketingManagers);

      setAgencies(agencies);
    } catch (error: any) {
      console.log(error.message);
      dispatch(
        toggleAlert({
          toggle: true,
          message: error.response.data.message,
          type: 'error',
        }),
      );
    } finally {
      setFetchLoading(false);
    }
  };

  const getDiySettings = async (brand: Brand) => {
    try {
      const response = await getSingleDiySetting(brand?._id);

      setDiySettingForm(response.data);
      if (response.data.defaultUser) {
        setValue('defaultUser', response.data.defaultUser._id);
      }
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const getBrandDiySettings = async (brand: Brand) => {
    try {
      const response = await getSingleDiySetting(brand?._id);

      const data: DiySettings = response?.data;

      setBrandDiySettings(data);
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const handleOnSubmit = async () => {
    try {
      setLoading(true);

      setIsDirty(false);

      let brandFeePayload: Fee[] = [];
      let locationFeePayload: Fee[] = [];
      let obj: Brand = {
        investmentRequirement,
      };

      Object.keys(formValues).forEach((key) => {
        if (formValues[key as keyof Brand] !== '') {
          obj = { ...obj, [key]: formValues[key as keyof Brand] };
        }
      });

      if (isEdit) {
        const response = await editBrand(
          {
            ...obj,
            diy: IS_DIY_ADZ,
          },
          brand?._id,
        );

        setLoading(false);

        let temp: Brand[] = [];
        brands.forEach((brand: Brand) => {
          if (brand?._id === response.updatedBrand._id) {
            brand = response.updatedBrand;
          }

          temp = [...temp, brand];
        });
        dispatch(setBrand(response.updatedBrand));
        dispatch(setBrands(temp));

        brandFeesForm.forEach((fee: Fee) => {
          fee.ref = response.updatedBrand._id;
          brandFeePayload = [...brandFeePayload, fee];
        });

        locationFeesForm.forEach((fee: Fee) => {
          fee.ref = response.updatedBrand._id;
          locationFeePayload = [...locationFeePayload, fee];
        });

        if (IS_DIY_ADZ) setupDiySetting(response.updatedBrand._id);

        dispatch(
          toggleAlert({
            toggle: true,
            message: 'Brand updated successfully',
          }),
        );
      } else {
        const { brand } = await createBrand({
          ...obj,
          diy: IS_DIY_ADZ,
        });

        let temp: Brand[] = [...brands, brand];
        dispatch(setBrand(brand));
        dispatch(setBrands(temp));

        brandFeesForm.forEach((fee: Fee) => {
          fee.ref = brand._id;
          brandFeePayload = [...brandFeePayload, fee];
        });

        locationFeesForm.forEach((fee: Fee) => {
          fee.ref = brand._id;
          locationFeePayload = [...locationFeePayload, fee];
        });

        if (IS_DIY_ADZ) setupDiySetting(brand._id);

        dispatch(
          toggleAlert({
            toggle: true,
            message: 'Brand created successfully',
          }),
        );

        const redirectUrl = IS_DIY_ADZ ? '/reportz/kpiz' : '/dashboard';

        navigate(redirectUrl);

        handleCreateOrUpdateFees('brand', brandFeePayload);
      }

      handleCreateOrUpdateFees('location', locationFeePayload);
    } catch (error: any) {
      setIsDirty(true);

      const errorMsg = errorMessageParser(error);

      dispatch(
        toggleAlert({
          toggle: true,
          message: errorMsg,
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdateFees = async (entity: string, payload: Fee[]) => {
    try {
      const IS_BRAND = entity === 'brand';
      const IS_LOCATION = entity === 'location';

      const response = await createOrUpdateFee(payload);

      if (IS_BRAND) {
        setBrandFeesForm(response?.data || []);
      } else if (IS_LOCATION) {
        setLocationFeesForm(response?.data || []);
      }
    } catch (error: any) {
      dispatch(
        toggleAlert({
          toggle: true,
          message: error.message,
          type: 'error',
        }),
      );
    }
  };

  const setupDiySetting = async (brandId: string) => {
    try {
      const response = await createOrUpdateDiySettings(brandId, diySettingForm);

      setDiySettingForm(response.data);
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const handleInviteSalesperson = async (email: string) => {
    try {
      if (!formValues?.agency?._id) {
        return dispatch(
          toggleAlert({
            toggle: true,
            message:
              'Missing Agency: Please select an agency before inviting users',
            type: 'error',
          }),
        );
      }

      setInviteLoading(true);
      const response = await inviteSalesperson({
        email,
        clientUri: window.location.host,
        diy: IS_DIY_ADZ,
        agencyId: formValues?.agency?._id,
      });
      setOpenInviteModal(false);

      let users: any[] = [];
      formValues?.users.forEach((user: any) => {
        if (user.email === email) {
          user.invitedAt = response?.data?.invitedAt;
        }

        users = [...users, user];
      });

      setFormValues({ ...formValues, users });

      dispatch(
        toggleAlert({
          toggle: true,
          message: response?.message,
        }),
      );
    } catch (error: any) {
      const err = error.response.data;

      setInvitationErrors({
        ...invitationErrors,
        email: err.email,
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleOpenSalespersonForm = () => {
    setShowSalespersonForm(true);
  };

  const handleCloseSalespersonForm = () => {
    setShowSalespersonForm(false);
  };

  const handleOpenManagerForm = () => {
    setShowManagerForm(true);
  };

  const handleCloseManagerForm = () => {
    setShowManagerForm(false);
  };

  const handleOpenMarketingManagerForm = (index: number) => {
    setIsDirty(true);
    let userForms: boolean[] = [...showMultipleUserForms];

    userForms[index] = true;

    setShowMultipleUserForms(userForms);
  };

  const handleCloseMarketingManagerForm = (index: number) => {
    let userForms: boolean[] = [...showMultipleUserForms];

    userForms[index] = false;

    setShowMultipleUserForms(userForms);
  };

  const handleOpenInviteModal = (info: InviteInfo) => {
    setOpenInviteModal(true);
    setInvitationInfo({
      ...invitationInfo,
      name: info.name,
      email: info.email,
      id: info.id,
      role: info.role,
    });
  };

  const handleCloseInviteModal = () => {
    setInvitationErrors({
      ...invitationErrors,
      email: '',
    });
    setOpenInviteModal(false);
  };

  const resetDiyForm = () => {
    setDiySettingForm({
      ref: brand?._id,
      docModel: 'Brand',
      allowTrafficAds: true,
      allowLeadAds: true,
      allowMessengerAds: false,
      allowAppAds: false,
      allowAllGeolocation: true,
      locationTypes: [],
      excludedLocationTypes: [],
      excludedGeolocations: null,
      excludeGeolocation: false,
      defaultUrl: '',
      defaultEmploymentUrl: '',
      defaultCalendarUrl: '',
      defaultPrivacyPolicyUrl: '',
      appDeepLink: '',
      maxCityRadius: 50,
      maxAddressRadius: 50,
      allowSetupOwnAccount: false,
      geolocation: null,
      facebookPage: null,
      instagramAccount: null,
      fbAdAccount: null,
      defaultLocation: null,
      defaultUser: null,
      targetCountries: [{ code: 'US', label: 'United States', phone: '1' }],
    });
  };

  const resetForm = () => {
    setFormValues({
      brand: '',
      acronym: '',
      allLeads: 'No',
      zipcodereq: 'No',
      financialreq: 'Yes',
      redirect: 'No',
      redirectlink: '',
      redirecttext: '',
      industryRequirement: 'No',
      schedulecall: 'No',
      industryQuestion: '',
      calendly: '',
      salesperson: {
        name: '',
        _id: '',
        email: '',
        role: '',
        user: {
          email: '',
        },
      },
      salespersonTitle: '',
      page: null,
      phoneNumReq: 'No',
      budgetOffset: 100,
      cannedResponses: [
        {
          message: `Hi <<lead>>! Good day, I'm <<salesperson>> of <<brand>>, how can I help you?`,
          isDeleted: false,
        },
        {
          message: `Hi <<lead>> you want to schedule a call? Here's my Calendly link <<calendly_link>>.`,
          isDeleted: false,
        },
        {
          message:
            'I am glad I was able to help. Please tell me if you have any more queries 🙂',
          isDeleted: false,
        },
      ],
      manager: {
        name: '',
        _id: '',
        email: '',
        role: '',
        user: {
          email: '',
        },
      },
      active: true,
      paused: false,
      lastPausedAt: null,
      facebookBusinessIds: [],
      googleCustomerIds: [],
      allowLocations: false,
      agency: {
        _id: '',
        agency: '',
      },
      __type: 'brand',
      allowSetupDepositAccount: false,
      allowSetupPayment: false,
      analyticTools: [AnalyticTools.TRENDZ],
      locationLabel: 'location',
      industry: '',
      businessType: BusinessType.FRANDEV,
      diy: IS_DIY_ADZ,
      phoneNumber: '',
      address: {
        line1: '',
        city: '',
        state: '',
        zipcode: '',
        lat: null,
        lng: null,
      },
      users: [],
      chargeMediaFee: false,
      platform: [],
    });
  };

  const handleConfirmNavigation = () => {
    autoUnblockingTx.retry();
    setOpenConfirmNavModal(false);
  };

  const handleCancelNavigation = () => {
    setOpenConfirmNavModal(false);
  };

  const handleRemoveUser = (index: number) => {
    setIsDirty(true);
    let showForms: boolean[] = [...showMultipleUserForms];
    let users: Salesperson[] = [...formValues?.users];
    if (users[index]) {
      if (users[index]?._id === diySettingForm?.defaultUser?._id) {
        setDiySettingForm({ ...diySettingForm, defaultUser: null });
      }

      users.splice(index, 1);
      setFormValues({ ...formValues, users });
    }

    showForms.splice(index, 1);
    setFormCount((prev) => prev - 1);
    clearErrors(`user-${index + 1}`);
  };

  const handleAddUser = () => {
    setIsDirty(true);
    setShowMultipleUserForms([...showMultipleUserForms, false]);
    setFormCount((prev) => prev + 1);
  };

  const handleAllowOwnAdAccount = (e: ChangeEventType) => {
    setIsDirty(true);
    clearErrors('defaultUser');
    let temp: DiySettings = {
      ...diySettingForm,
      [e.target.name]: e.target.value === 'no',
    };

    if (e.target.value === 'no') {
      temp = {
        ...temp,
        fbAdAccount: null,
        facebookPage: null,
        instagramAccount: null,
        defaultUser: withAgencyAccess ? formValues?.agency?.defaultUser : null,
      };

      setFormValues({ ...formValues, facebookBusinessIds: [] });
    } else if (e.target.value === 'no' && formValues?.__type === 'location') {
      temp = {
        ...temp,
        fbAdAccount: brandDiySettings?.fbAdAccount,
        facebookPage: brandDiySettings?.facebookPage,
        instagramAccount: brandDiySettings?.instagramAccount,
        defaultUser: withAgencyAccess ? formValues?.agency?.defaultUser : null,
      };
    }

    setDiySettingForm(temp);
  };

  const multipleUserForm = () => {
    let components: any[] = [];

    for (let i = 0; i < formCount; i++) {
      const disabled = (formValues?.users || [])[i]?._id === roleBasedId;

      components = [
        ...components,
        <>
          <Grid item key={i + 1} xs={12}>
            <p className={styles.label}>{`User #${i + 1}`}</p>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Autocomplete
                  disabled={disabled}
                  id={`user-${i + 1}`}
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={option._id}>
                        {`${option.salespersonId}. ${option.name}`}
                      </li>
                    );
                  }}
                  options={salespersons.sort(
                    (a, b) => -b.name?.localeCompare(a.name),
                  )}
                  value={(formValues?.users || [])[i] || null}
                  getOptionLabel={(option) => `${option.name}`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...register(`user-${i + 1}`, {
                        required: true,
                      })}
                      id={`user-${i + 1}`}
                      name={`user-${i + 1}`}
                      variant="standard"
                      label="Name"
                      placeholder="Select a user"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      error={!!errors?.[`user-${i + 1}`]}
                      helperText={
                        !!errors?.[`user-${i + 1}`] &&
                        `User #${i + 1} is required`
                      }
                    />
                  )}
                  disableClearable
                  onChange={async (e: any, value: any) => {
                    setIsDirty(true);
                    clearErrors(`user-${i + 1}`);

                    let users: any[] = [...(formValues?.users || [])];

                    while (users.length < i) {
                      users.push(null);
                    }

                    users[i] = value;

                    setFormValues({ ...formValues, users });
                    setValue(`user-${i + 1}`, value?._id);
                  }}
                  getOptionDisabled={(option) => {
                    const ids = formValues?.users?.map((user: any) => {
                      return user?._id;
                    });

                    return ids?.includes(option._id);
                  }}
                />
              </Grid>

              <Grid
                item
                xs={6}
                sx={{
                  justifyContent: 'left',
                  alignItems: 'left',
                  display: 'flex',
                }}
              >
                {!showMultipleUserForms[i] ? (
                  <PrimaryButton
                    title={`${
                      (formValues?.users || [])[i] ? 'Edit' : 'Create'
                    } User`}
                    type="button"
                    variant="text"
                    startIcon={<PersonAddAltOutlinedIcon />}
                    fontSize="12px"
                    handleOnClick={() => handleOpenMarketingManagerForm(i)}
                    disabled={disabled}
                  />
                ) : null}

                <PrimaryButton
                  title="Remove User"
                  type="button"
                  variant="text"
                  startIcon={<GroupRemoveOutlinedIcon />}
                  fontSize="12px"
                  handleOnClick={() => handleRemoveUser(i)}
                  disabled={disabled}
                />

                {!(formValues.users || [])[i]?._id ||
                (formValues.users || [])[i]?.user?.email ? (
                  <PrimaryButton
                    title="Invite Accepted"
                    type="button"
                    variant="text"
                    startIcon={<ContactMailOutlinedIcon />}
                    fontSize="12px"
                    disabled
                  />
                ) : (
                  <PrimaryButton
                    title={
                      (formValues.users || [])[i]?.invitedAt
                        ? 'Resend Invite'
                        : 'Invite User'
                    }
                    type="button"
                    variant="text"
                    startIcon={<ContactMailOutlinedIcon />}
                    fontSize="12px"
                    handleOnClick={() => {
                      handleOpenInviteModal({
                        ...formValues?.users[i],
                      } as InviteInfo);
                    }}
                    disabled={disabled}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>

          {showMultipleUserForms[i] ? (
            <Grid item xs={12}>
              <BrandUserForm
                isLocation={false}
                salespersons={salespersons}
                setSalespersons={setSalespersons}
                marketingManagers={marketingManagers}
                setMarketingManagers={setMarketingManagers}
                brandFormValues={formValues}
                setBrandFormValues={setFormValues}
                onCloseManagerForm={handleCloseManagerForm}
                onCloseSalespersonForm={handleCloseSalespersonForm}
                onCloseMultipleForm={handleCloseMarketingManagerForm}
                role="marketing-manager"
                multiple
                index={i}
                edit={(formValues?.users || [])[i] ? true : false}
                salespersonId={
                  (formValues?.users || [])[i]
                    ? (formValues?.users || [])[i]._id
                    : null
                }
                capabilities={capabilities}
                isAdmin={isSuperAdmin || isAdmin}
                isMarketingManager={isMarketingManager}
                canCreateBrand={canCreateBrand}
              />
            </Grid>
          ) : null}
        </>,
      ];
    }

    return components;
  };

  return (
    <Box
      className={`${styles.form} ${XsOnly() ? `${styles['-mobile']}` : ''}`}
      justifyContent="center"
    >
      <CircularLoading loading={fetchLoading} />

      <PageHeader title={isEdit ? 'Edit Brand' : 'Add New Brand'} />

      {/** BRAND DETAILS SECTION */}
      {canCreateBrand || isSuperAdmin || isAdmin ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                {...register('brand', {
                  required: true,
                })}
                fullWidth
                required
                variant="standard"
                type="text"
                id="brand"
                name="brand"
                label="Brand"
                onChange={(e: ChangeEventType) => {
                  clearErrors('brand');
                  setIsDirty(true);
                  setFormValues({ ...formValues, brand: e.target.value });
                }}
                value={formValues.brand}
                InputLabelProps={{ shrink: true }}
                size="small"
                autoFocus
                error={!!errors?.brand}
                helperText={errors?.brand && 'Brand name is required'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                id="agency"
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option._id}>
                      {option.agency}
                    </li>
                  );
                }}
                options={agencies.sort(
                  (a, b) => -b.agency?.localeCompare(a.agency),
                )}
                value={formValues.agency}
                getOptionLabel={(option) => option.agency}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...register('agency', {
                      required: true,
                    })}
                    variant="standard"
                    label="Agency"
                    fullWidth
                    size="small"
                    placeholder="Select an agency"
                    required
                    name="agency"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors?.agency}
                    helperText={errors?.agency && 'Agency is required'}
                  />
                )}
                disableClearable
                onChange={async (e: any, value: any) => {
                  setIsDirty(true);
                  setFormValues({
                    ...formValues,
                    agency: {
                      ...value,
                    },
                  });

                  if (!(isAdmin || isSuperAdmin) && withAgencyAccess) {
                    setDiySettingForm({
                      ...diySettingForm,
                      defaultUser: value,
                    });
                  }
                  clearErrors('agency');
                  setValue('agency', value._id);
                }}
                disabled={agencies.length === 1}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} my={1}>
            <Grid item xs={12} md={6}>
              <TextField
                {...register('acronym', {
                  required: true,
                  maxLength: {
                    value: ACRONYM_MAX_LENGTH,
                    message: 'Brand short name is too long',
                  },
                  minLength: {
                    value: ACRONYM_MIN_LENGTH,
                    message: 'Brand short name should be at least 3 characters',
                  },
                })}
                fullWidth
                required
                variant="standard"
                type="text"
                id="acronym"
                name="acronym"
                label="Brand Short Name"
                onChange={(e: ChangeEventType) => {
                  setIsDirty(true);
                  clearErrors('acronym');
                  setFormValues({ ...formValues, acronym: e.target.value });
                }}
                value={formValues?.acronym}
                InputLabelProps={{ shrink: true }}
                size="small"
                error={!!errors?.acronym}
                helperText={
                  errors?.acronym?.message ||
                  (errors?.acronym && 'Brand short name is required')
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="caption">
                        {`${
                          formValues?.acronym?.length || 0
                        }/${ACRONYM_MAX_LENGTH}`}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  maxLength: ACRONYM_MAX_LENGTH,
                  minLegth: ACRONYM_MIN_LENGTH,
                }}
              />
            </Grid>
          </Grid>

          {!IS_DIY_ADZ ? (
            <>
              <p className={styles.label}>Salesperson:</p>

              <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={7}>
                      <Autocomplete
                        id="salesperson"
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={option._id}>
                              {`${option.salespersonId}. ${option.name}`}
                            </li>
                          );
                        }}
                        options={salespersons.sort(
                          (a, b) => -b.name?.localeCompare(a.name),
                        )}
                        value={formValues?.salesperson}
                        getOptionLabel={(option) => `${option.name}`}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            label="Name"
                            fullWidth
                            size="small"
                            placeholder="Select a salesperson"
                            required
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                        disableClearable
                        onChange={async (e: any, value: any) => {
                          setIsDirty(true);
                          setFormValues({
                            ...formValues,
                            salesperson: { ...value },
                          });
                        }}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      lg={5}
                      sx={{
                        justifyContent: 'left',
                        alignItems: 'left',
                        display: 'flex',
                      }}
                    >
                      {!showSalespersonForm ? (
                        <PrimaryButton
                          title="Add User"
                          type="button"
                          startIcon={<AddIcon />}
                          fontSize="12px"
                          handleOnClick={handleOpenSalespersonForm}
                        />
                      ) : null}

                      {!formValues.salesperson?._id ||
                      formValues.salesperson?.user?.email ? (
                        <PrimaryButton
                          title="Invite Accepted"
                          type="button"
                          variant="text"
                          startIcon={<ContactMailOutlinedIcon />}
                          fontSize="12px"
                          disabled
                        />
                      ) : (
                        <PrimaryButton
                          title={
                            formValues?.salesperson?.invitedAt
                              ? 'Resend Invite'
                              : 'Invite User'
                          }
                          type="button"
                          variant="text"
                          startIcon={<ContactMailOutlinedIcon />}
                          fontSize="12px"
                          handleOnClick={() => {
                            handleOpenInviteModal({
                              ...formValues?.salesperson,
                            } as InviteInfo);
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="standard"
                    type="text"
                    id="salespersonTitle"
                    name="salespersonTitle"
                    label="Title"
                    onChange={(e: ChangeEventType) => {
                      setIsDirty(true);
                      setFormValues({
                        ...formValues,
                        salespersonTitle: e.target.value,
                      });
                    }}
                    value={formValues.salespersonTitle}
                    placeholder="Sales Manager"
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
              </Grid>
            </>
          ) : null}

          {showSalespersonForm ? (
            <Grid container spacing={2} mb={1}>
              <Grid item xs={12}>
                <BrandUserForm
                  isLocation={false}
                  salespersons={salespersons}
                  setSalespersons={setSalespersons}
                  managers={managers}
                  setManagers={setManagers}
                  brandFormValues={formValues}
                  setBrandFormValues={setFormValues}
                  role="salesperson"
                  onCloseSalespersonForm={handleCloseSalespersonForm}
                  onCloseManagerForm={handleCloseManagerForm}
                />
              </Grid>
            </Grid>
          ) : null}

          {!IS_DIY_ADZ ? (
            <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
              <Grid item xs={12} md={6}>
                <p className={styles.label}>Manager:</p>

                <Grid container spacing={2}>
                  <Grid item xs={12} lg={7}>
                    <Autocomplete
                      id="manager"
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option._id}>
                            {`${option.salespersonId}. ${option.name}`}
                          </li>
                        );
                      }}
                      options={managers.sort(
                        (a, b) => -b.name?.localeCompare(a.name),
                      )}
                      value={formValues?.manager}
                      getOptionLabel={(option) => `${option.name}`}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          label="Name"
                          placeholder="Select a manager"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                        />
                      )}
                      disableClearable
                      onChange={async (e: any, value: any) => {
                        setIsDirty(true);
                        setFormValues({
                          ...formValues,
                          manager: { ...value },
                        });
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    lg={5}
                    sx={{
                      justifyContent: 'left',
                      alignItems: 'left',
                      display: 'flex',
                    }}
                  >
                    {!showManagerForm ? (
                      <PrimaryButton
                        title="Create User"
                        type="button"
                        variant="text"
                        startIcon={<AddIcon />}
                        fontSize="12px"
                        handleOnClick={handleOpenManagerForm}
                      />
                    ) : null}

                    {!formValues.manager?._id ||
                    formValues.manager?.user?.email ? (
                      <PrimaryButton
                        title="Invite Accepted"
                        type="button"
                        variant="text"
                        startIcon={<ContactMailOutlinedIcon />}
                        fontSize="12px"
                        disabled
                      />
                    ) : (
                      <PrimaryButton
                        title={
                          formValues?.manager?.invitedAt
                            ? 'Resend Invite'
                            : 'Invite User'
                        }
                        type="button"
                        variant="text"
                        startIcon={<ContactMailOutlinedIcon />}
                        fontSize="12px"
                        handleOnClick={() => {
                          handleOpenInviteModal({
                            ...formValues?.manager,
                          } as InviteInfo);
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : null}

          {showManagerForm ? (
            <Grid container spacing={2} mb={1}>
              <Grid item xs={12}>
                <BrandUserForm
                  isLocation={false}
                  salespersons={salespersons}
                  setSalespersons={setSalespersons}
                  managers={managers}
                  setManagers={setManagers}
                  brandFormValues={formValues}
                  setBrandFormValues={setFormValues}
                  onCloseManagerForm={handleCloseManagerForm}
                  onCloseSalespersonForm={handleCloseSalespersonForm}
                  role="brand-manager"
                />
              </Grid>
            </Grid>
          ) : null}

          {IS_DIY_ADZ ? (
            <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
              {multipleUserForm()}

              <Grid item xs={12}>
                {formCount !== MAX_USER_COUNT ? (
                  <PrimaryButton
                    title="Add User"
                    type="button"
                    handleOnClick={handleAddUser}
                    startIcon={<AddIcon />}
                  />
                ) : null}
              </Grid>
            </Grid>
          ) : null}

          <div>
            <p className={styles.label}>Campaign Setup</p>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('budgetOffset', {
                    required: true,
                  })}
                  required
                  fullWidth
                  variant="standard"
                  type="number"
                  id="budgetOffset"
                  name="budgetOffset"
                  label="Budget Offset"
                  onChange={(e: ChangeEventType) => {
                    setIsDirty(true);
                    clearErrors('budgetOffset');
                    setFormValues({
                      ...formValues,
                      budgetOffset: parseFloat(e.target.value),
                    });
                  }}
                  value={formValues.budgetOffset}
                  placeholder="0"
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                      {
                        display: 'none',
                      },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  error={!!errors?.budgetOffset}
                  helperText={
                    errors?.budgetOffset && 'Budget offset is required'
                  }
                />
              </Grid>
            </Grid>
          </div>

          <Grid container spacing={2} my={1}>
            <Grid item xs={12} sm={6}>
              <FormControl>
                <Typography variant="body2">Brand Status</Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formValues.active}
                      name="active"
                      onChange={(e: any) => {
                        setIsDirty(true);
                        setFormValues({
                          ...formValues,
                          [e.target.name]: e.target.checked,
                        });
                      }}
                    />
                  }
                  label={formValues.active ? 'Active' : 'Inactive'}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl>
                <Typography variant="body2">Campaign Status</Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={!formValues.paused}
                      name="paused"
                      onChange={(e: any) => {
                        setIsDirty(true);
                        setFormValues({
                          ...formValues,
                          [e.target.name]: !e.target.checked,
                        });
                      }}
                    />
                  }
                  label={!formValues.paused ? 'Running' : 'Paused'}
                />
              </FormControl>
            </Grid>
          </Grid>

          {formValues?.agency?.allowSetupPayment ? (
            <>
              <Grid item xs={12} my={1}>
                <Divider />
              </Grid>

              <p className={styles.label}>Payment Setup</p>

              <Grid container spacing={2} mb={1}>
                <Grid item xs={12} md={6}>
                  <FormControl>
                    <Typography variant="body2">
                      Allow Credit Card Setup
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={formValues.allowSetupPayment}
                          name="allowSetupPayment"
                          onChange={(e: any) => {
                            setIsDirty(true);
                            clearErrors('budgetPercentageFee');
                            setFormValues({
                              ...formValues,
                              [e.target.name]: e.target.checked,
                            });
                          }}
                        />
                      }
                      label={
                        formValues.allowSetupPayment || false
                          ? 'Enabled'
                          : 'Disabled'
                      }
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {formValues.allowSetupPayment && IS_DIY_ADZ ? (
                <>
                  <Grid container spacing={2} my={1}>
                    <Grid item xs={12}>
                      <TextField
                        {...register('budgetPercentageFee', {
                          required: true,
                        })}
                        variant="standard"
                        type="number"
                        name="budgetPercentageFee"
                        label={`Subscription Fee Percentage`}
                        onChange={(e: ChangeEventType) => {
                          setIsDirty(true);
                          clearErrors('budgetPercentageFee');
                          setFormValues({
                            ...formValues,
                            budgetPercentageFee: parseFloat(e.target.value),
                          });
                        }}
                        value={formValues?.budgetPercentageFee}
                        placeholder="18"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                        sx={{
                          marginRight: '10px',
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                            {
                              display: 'none',
                            },
                          '& input[type=number]': {
                            MozAppearance: 'textfield',
                          },
                        }}
                        error={!!errors?.budgetPercentageFee}
                        helperText={
                          errors?.budgetPercentageFee &&
                          'Budget percentage fee is required'
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={1}>
                    <Grid item xs={12} md={6}>
                      <FormControl>
                        <Typography variant="body2">
                          How will the brand pay for media?
                        </Typography>

                        <RadioGroup
                          row
                          aria-labelledby="chargeMediaFee"
                          name="chargeMediaFee"
                          value={
                            formValues.chargeMediaFee
                              ? 'via_credit_card'
                              : 'via_facebook'
                          }
                          onChange={(e: ChangeEventType) => {
                            setIsDirty(true);
                            setFormValues({
                              ...formValues,
                              [e.target.name]:
                                e.target.value === 'via_credit_card',
                            });
                          }}
                          defaultValue="No"
                        >
                          <FormControlLabel
                            value="via_facebook"
                            control={<Radio size="small" />}
                            label="Directly to Facebook"
                          />
                          <FormControlLabel
                            value="via_credit_card"
                            control={<Radio size="small" />}
                            label="Immediately via credit card"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Grid>
                </>
              ) : null}

              <Grid container spacing={2} mb={1}>
                <Grid item xs={12} md={6}>
                  <FormControl>
                    <Typography variant="body2">
                      Enable Deposit Account
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={formValues.allowSetupDepositAccount}
                          name="allowSetupDepositAccount"
                          onChange={(e: any) => {
                            setIsDirty(true);
                            setFormValues({
                              ...formValues,
                              [e.target.name]: e.target.checked,
                            });
                          }}
                        />
                      }
                      label={
                        formValues.allowSetupDepositAccount || false
                          ? 'Enabled'
                          : 'Disabled'
                      }
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </>
          ) : null}

          <Grid item xs={12} mt={2} mb={1}>
            <Divider />
          </Grid>

          <p className={styles.label}>Location Setup</p>

          <Grid container spacing={2} mb={1}>
            <Grid item xs={12} md={6}>
              <FormControl>
                <Typography variant="body2">
                  Allow Multiple Locations
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formValues.allowLocations}
                      name="allowLocations"
                      onChange={(e: any) => {
                        setIsDirty(true);
                        setFormValues({
                          ...formValues,
                          [e.target.name]: e.target.checked,
                        });
                      }}
                    />
                  }
                  label={
                    formValues.allowLocations || false ? 'Enabled' : 'Disabled'
                  }
                />
              </FormControl>
            </Grid>

            {formValues.allowLocations ? (
              <Grid item xs={12} md={6}>
                <FormControl>
                  <Typography variant="body2">
                    Allow Locations Setup Payment
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formValues.allowLocationsSetupPayment}
                        name="allowLocationsSetupPayment"
                        onChange={(e: any) => {
                          setIsDirty(true);
                          setFormValues({
                            ...formValues,
                            [e.target.name]: e.target.checked,
                          });
                        }}
                      />
                    }
                    label={
                      formValues.allowLocationsSetupPayment || false
                        ? 'Enabled'
                        : 'Disabled'
                    }
                  />
                </FormControl>
              </Grid>
            ) : null}
          </Grid>

          <Grid item xs={12} mb={1}>
            <Divider />
          </Grid>

          <Grid container spacing={2}>
            {[adsProvider.FACEBOOK, adsProvider.GOOGLE].map(
              (platform: string) => {
                return (
                  <Grid item xs={12}>
                    <FormControl key={platform}>
                      <Typography variant="body2">{`${humanizeString(
                        platform,
                      )} Ads`}</Typography>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={formValues?.platform?.includes(platform)}
                            name="platform"
                            onChange={(e: any) => {
                              let platforms: string[] = [
                                ...(formValues?.platform || []),
                              ];
                              if (e.target.checked) {
                                platforms = [...platforms, platform];
                              } else {
                                platforms = platforms.filter(
                                  (value: string) => value !== platform,
                                );
                              }
                              setIsDirty(true);
                              setFormValues({
                                ...formValues,
                                [e.target.name]: platforms,
                              });
                            }}
                          />
                        }
                        label={
                          formValues?.platform?.includes(platform)
                            ? 'Enabled'
                            : 'Disabled'
                        }
                      />
                    </FormControl>
                  </Grid>
                );
              },
            )}

            {formValues?.platform?.includes(adsProvider.FACEBOOK) ? (
              <>
                <Grid item xs={12}>
                  <FormControl>
                    <Typography variant="body2">
                      Connect the Ad Account and Facebook Page now?
                    </Typography>

                    <RadioGroup
                      row
                      aria-labelledby="allowSetupOwnAccount"
                      name="allowSetupOwnAccount"
                      value={
                        diySettingForm?.allowSetupOwnAccount ? 'no' : 'yes'
                      }
                      onChange={handleAllowOwnAdAccount}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio size="small" />}
                        label="Yes"
                      />

                      <FormControlLabel
                        value="no"
                        control={<Radio size="small" />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {diySettingForm?.allowSetupOwnAccount ? (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          renderOption={(props, option) => {
                            return (
                              <li {...props} key={option._id}>
                                {`${option.salespersonId}. ${option.name}`}
                              </li>
                            );
                          }}
                          options={formValues?.users?.sort(
                            (a, b) => -b.name?.localeCompare(a.name),
                          )}
                          value={
                            formValues?.users?.find(
                              (user: Salesperson) =>
                                user._id === diySettingForm?.defaultUser?._id,
                            ) || null
                          }
                          getOptionLabel={(option) => `${option.name}`}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              {...register('defaultUser', {
                                required: true,
                              })}
                              variant="standard"
                              label="Default User"
                              name="defaultUser"
                              placeholder="Select default user"
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                              error={!!errors?.defaultUser}
                              helperText={
                                (errors?.defaultUser &&
                                  'Default user is required') ||
                                `Please select the user who has full access to 
                                 both your Ad Account and Facebook page. The
                                 selected user needs to connect their Facebook
                                 account to the platform.`
                              }
                            />
                          )}
                          disableClearable
                          onChange={async (e: any, value: any) => {
                            setIsDirty(true);
                            clearErrors('defaultUser');
                            setDiySettingForm({
                              ...diySettingForm,
                              defaultUser: {
                                ...value,
                              },
                            });
                            setValue('defaultUser', value?._id);
                          }}
                          getOptionDisabled={(option) => {
                            return (
                              option._id === diySettingForm?.defaultUser?._id
                            );
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null}
              </>
            ) : null}
          </Grid>

          <Grid container spacing={2} mb={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    id="businessType"
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option}>
                          {option}
                        </li>
                      );
                    }}
                    options={businessTypes}
                    value={formValues?.businessType}
                    getOptionLabel={(option) => option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        {...register('businessType', {
                          required: true,
                        })}
                        variant="standard"
                        label="Business Type"
                        fullWidth
                        size="small"
                        name="businessType"
                        placeholder="Select a business type"
                        required
                        InputLabelProps={{ shrink: true }}
                        error={!!errors?.businessType}
                        helperText={
                          errors?.businessType && 'Business type is required'
                        }
                      />
                    )}
                    disableClearable
                    onChange={async (e: any, value: any) => {
                      setIsDirty(true);
                      clearErrors('businessType');
                      setFormValues({
                        ...formValues,
                        businessType: value,
                      });
                      setValue('businessType', value);
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    id="industry"
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option}>
                          {option}
                        </li>
                      );
                    }}
                    options={brandIndustries}
                    value={formValues?.industry}
                    getOptionLabel={(option) => option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        {...register('industry', {
                          required: true,
                        })}
                        variant="standard"
                        label="Industry"
                        fullWidth
                        size="small"
                        name="industry"
                        placeholder="Select an industry"
                        required
                        InputLabelProps={{ shrink: true }}
                        error={!!errors?.industry}
                        helperText={errors?.industry && 'Industry is required'}
                      />
                    )}
                    disableClearable
                    onChange={async (e: any, value: any) => {
                      setIsDirty(true);
                      clearErrors('industry');
                      setFormValues({
                        ...formValues,
                        industry: value,
                      });
                      setValue('industry', value);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider />
        </>
      ) : null}

      {IS_DIY_ADZ &&
      capabilities?.addUser &&
      !(isSuperAdmin || isAdmin || canCreateBrand) ? (
        <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
          {multipleUserForm()}

          <Grid item xs={12}>
            {formCount !== MAX_USER_COUNT ? (
              <PrimaryButton
                title="Add User"
                type="button"
                handleOnClick={handleAddUser}
                startIcon={<AddIcon />}
              />
            ) : null}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      ) : null}

      <Box sx={{ paddingBottom: '80px' }}>
        <FloatingButton
          title={isEdit ? 'Save' : 'Add'}
          onClick={handleSubmit(handleOnSubmit)}
          loading={loading}
          bottom="60px"
          right="100px"
          fixed
        />
      </Box>

      <InviteModal
        title={`Invite as ${humanizeString(invitationInfo.role)}`}
        subtitle={`Are you sure you want to invite ${
          invitationInfo.name
        } as ${humanizeString(invitationInfo.role)}?`}
        open={openInviteModal}
        handleClose={handleCloseInviteModal}
        invitationInfo={invitationInfo}
        handleInvite={handleInviteSalesperson}
        loading={inviteLoading}
        errors={invitationErrors}
      />

      <ConfirmNavigationModal
        open={openConfirmNavModal}
        onClose={handleCancelNavigation}
        onConfirm={handleConfirmNavigation}
      />
    </Box>
  );
};

export default BrandForm;
